import { useEffect, useRef, useState } from 'react';
import { RotateCcw, RotateCw } from 'lucide-react';
import { Button } from '@higgins/ui';
import { bearingToDirection } from '../lib/compass';
import { PIN_EDGE, PIN_FONT, type PlanPin } from '../lib/marked-up-plan';
import type { SitePlanDoc } from '../lib/site-plan';
import { bearingTowards, pinShape, pointAlong, wholeBearing } from '../lib/site-plan-math';
import { TOUCH_TARGET_PX, useUnitsPerPx } from '../lib/plan-scale';
import { PlanOverlay } from './plan-overlay';

/** The chosen pin's ring (Higgins charcoal). */
const CHOSEN_EDGE = '#252729';
/** Movement (px) under which a press on a pin is a tap (choose it), not a drag. */
const TAP_SLOP_PX = 8;
/** One press of a turn button (degrees). */
const TURN_STEP = 15;

type Point = { x: number; y: number };

/**
 * The plan with its pins. Drag a pin to move it; tap one to choose it, then
 * drag its round handle (or use the turn buttons) to set the way it faces.
 * With a photo chosen in the list (`placing`), tap the plan to put its pin
 * there. North is up: a Nearmap plan always is, and a picked image is assumed to be.
 */
export function SitePlanView({ plan, image, pins, placing, onPlace, onTurn }: { plan: SitePlanDoc; image: string; pins: PlanPin[]; placing: string | null; onPlace: (id: string, x: number, y: number) => void; onTurn: (id: string, bearing: number) => void }) {
  const svg = useRef<SVGSVGElement>(null);
  const [dragging, setDragging] = useState<{ id: string; x: number; y: number; from: Point; moved: boolean } | null>(null);
  const [turning, setTurning] = useState<{ id: string; bearing: number } | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const unitsPerPx = useUnitsPerPx(svg, plan.width);
  const { r, reach } = pinShape(plan.width, { x: 0, y: 0 }, 0);
  const handleR = Math.max(r * 0.8, (TOUCH_TARGET_PX / 3) * unitsPerPx);
  const handleHitR = Math.max(handleR, (TOUCH_TARGET_PX / 2) * unitsPerPx);
  const handleDistance = reach + handleR + r * 0.3;
  const chosenPin = pins.find((p) => p.id === chosen && p.x !== null);

  /** A pointer's place on the plan, 0–1 across and down. */
  function at(e: React.PointerEvent): Point {
    const box = svg.current!.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - box.left) / box.width)), y: Math.min(1, Math.max(0, (e.clientY - box.top) / box.height)) };
  }

  function onMove(e: React.PointerEvent) {
    if (turning) {
      const pin = pins.find((p) => p.id === turning.id);
      if (!pin || pin.x === null || pin.y === null) return;
      const p = at(e);
      setTurning({ id: turning.id, bearing: bearingTowards({ x: pin.x * plan.width, y: pin.y * plan.height }, { x: p.x * plan.width, y: p.y * plan.height }) });
    } else if (dragging) {
      const moved = dragging.moved || Math.hypot(e.clientX - dragging.from.x, e.clientY - dragging.from.y) > TAP_SLOP_PX;
      setDragging({ ...dragging, ...(moved ? at(e) : {}), moved });
    }
  }

  function onUp(e: React.PointerEvent) {
    if (turning) {
      onTurn(turning.id, turning.bearing);
      setTurning(null);
    } else if (dragging) {
      if (dragging.moved) onPlace(dragging.id, dragging.x, dragging.y);
      else setChosen(chosen === dragging.id ? null : dragging.id);
      setDragging(null);
    } else if (placing) {
      const p = at(e);
      onPlace(placing, p.x, p.y);
    } else setChosen(null);
  }

  // The chosen pin is drawn last, so its handle sits over the other pins.
  const drawOrder = [...pins.filter((p) => p.id !== chosen), ...pins.filter((p) => p.id === chosen)];

  return (
    <div className="space-y-2">
      <PlanOverlay
        plan={plan}
        image={image}
        svgRef={svg}
        crosshair={Boolean(placing)}
        label="Photo pins on the site plan"
        onPointerUp={onUp}
        onPointerMove={onMove}
        onPointerCancel={() => {
          setDragging(null);
          setTurning(null);
        }}
      >
          {drawOrder.map((pin) => {
            const moving = dragging?.id === pin.id && dragging.moved ? dragging : null;
            const x = moving?.x ?? pin.x;
            const y = moving?.y ?? pin.y;
            if (x === null || y === null) return null;
            const centre = { x: x * plan.width, y: y * plan.height };
            const bearing = turning?.id === pin.id ? turning.bearing : pin.bearing;
            const isChosen = pin.id === chosen;
            const shape = pinShape(plan.width, centre, bearing ?? 0);
            const handle = pointAlong(centre, bearing ?? 0, handleDistance);
            return (
              <g key={pin.id}>
                <g
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    (e.currentTarget as Element).setPointerCapture(e.pointerId);
                    setDragging({ id: pin.id, x, y, from: { x: e.clientX, y: e.clientY }, moved: false });
                  }}
                  className="cursor-grab"
                >
                  {(bearing !== null || isChosen) && (
                    <path
                      d={`M ${shape.arrow.map((p) => `${p.x} ${p.y}`).join(' L ')} Z`}
                      fill={bearing === null ? 'none' : pin.colour}
                      stroke={bearing === null ? pin.colour : PIN_EDGE}
                      strokeWidth={shape.arrowEdge}
                      strokeDasharray={bearing === null ? `${r * 0.3} ${r * 0.2}` : undefined}
                    />
                  )}
                  <circle cx={centre.x} cy={centre.y} r={r} fill={pin.colour} stroke={isChosen ? CHOSEN_EDGE : PIN_EDGE} strokeWidth={shape.ringWidth} />
                  <text x={centre.x} y={shape.textY} textAnchor="middle" fontSize={r} fontWeight={700} fill={PIN_EDGE} fontFamily={PIN_FONT}>
                    {pin.number}
                  </text>
                </g>
                {isChosen && (
                  <g
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      (e.currentTarget as Element).setPointerCapture(e.pointerId);
                      setTurning({ id: pin.id, bearing: bearing ?? 0 });
                    }}
                    className="cursor-grab"
                    aria-label={`Turn pin ${pin.number}`}
                  >
                    <circle cx={handle.x} cy={handle.y} r={handleHitR} fill="transparent" />
                    <circle cx={handle.x} cy={handle.y} r={handleR} fill={PIN_EDGE} stroke={pin.colour} strokeWidth={shape.ringWidth} />
                    <RotateCw x={handle.x - handleR * 0.6} y={handle.y - handleR * 0.6} width={handleR * 1.2} height={handleR * 1.2} color={CHOSEN_EDGE} />
                  </g>
                )}
              </g>
            );
          })}
      </PlanOverlay>
      {chosenPin && (
        <PinFacing
          pin={chosenPin}
          bearing={turning?.id === chosenPin.id ? turning.bearing : chosenPin.bearing}
          onTurn={(bearing) => onTurn(chosenPin.id, bearing)}
          onDone={() => setChosen(null)}
        />
      )}
    </div>
  );
}

/** The chosen pin's facing, with buttons to turn it: the way to set it where dragging the handle is fiddly (a phone). */
function PinFacing({ pin, bearing: saved, onTurn, onDone }: { pin: PlanPin; bearing: number | null; onTurn: (bearing: number) => void; onDone: () => void }) {
  // Quick taps add up: each turns from the last tap's bearing, not the saved one, which arrives a moment later (PR #489).
  // `sent` is every bearing tapped since the last settle; a saved value that isn't one of them came from elsewhere (the handle).
  const [pending, setPending] = useState<{ id: string; sent: number[] } | null>(null);
  const bearing = pending?.id === pin.id ? pending.sent[pending.sent.length - 1] : saved;
  useEffect(() => {
    if (pending && (pending.id !== pin.id || saved === null || saved === bearing || !pending.sent.includes(saved))) setPending(null);
  }, [pin.id, saved]); // eslint-disable-line react-hooks/exhaustive-deps -- settle once the saved bearing catches up
  const turn = (step: number) => {
    const next = wholeBearing((bearing ?? 0) + step);
    setPending({ id: pin.id, sent: [...(pending?.id === pin.id ? pending.sent : []), next] });
    onTurn(next);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold text-white" style={{ background: pin.colour }}>
        {pin.number}
      </span>
      <span className="min-w-0 flex-1">{bearing === null ? 'No facing yet: drag the round handle, or turn it.' : `Facing ${bearingToDirection(bearing)} (${bearing}°). Drag the round handle, or turn it.`}</span>
      <Button variant="outline" className="h-11 w-11 p-0" onClick={() => turn(-TURN_STEP)} aria-label={`Turn ${TURN_STEP}° left`}>
        <RotateCcw className="h-4 w-4" />
      </Button>
      <Button variant="outline" className="h-11 w-11 p-0" onClick={() => turn(TURN_STEP)} aria-label={`Turn ${TURN_STEP}° right`}>
        <RotateCw className="h-4 w-4" />
      </Button>
      <Button variant="ghost" className="h-11" onClick={onDone}>
        Done
      </Button>
    </div>
  );
}
